import React from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Button as MuiButton,
  Card,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { SlideGroupName } from 'generated/graphql';
import _ from 'lodash';
import {
  imageUtil,
  LoadingOverlay,
  routeDirectory
} from 'modules';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';

import * as dataService from './dataService';



const SlideHeaderBox = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between'
});

const SlideListBox = styled(Box)({
  display: 'flex',
  width: '100%',
  position: 'relative'
});

const RightMaskedStack = styled(Stack)({
  height: 360,
  alignItems: 'center',
  overflow: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
});

const RelativeImageWrapperBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2)
}));

const ShadowBox = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: '#C3D1D8',
  borderRadius: 5,
  transform: 'rotate(-4.2deg)',
  zIndex: -1
});

const ThumbnailBox = styled(Card)({
  width: 420,
  borderRadius: '5px',
  cursor: 'pointer'
});

const SlideHeaderTypography = styled(Typography)(({ theme }) => ({
}));

const Button = styled(MuiButton)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 700,
  marginRight: 5
}));

const CardRowBox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const NavButtonRight = styled(MuiButton)(({ theme })=>({
  backgroundColor: '#FFFFFF',
  color: '#87ACB9',
  background:'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 80%)',
  opacity: '0%',
  height: '85%',
  transition: 'opacity 0.2s',
  boxShadow: 'none',
  borderWidth: 0,
  '&:hover': {
    backgroundColor: '#FFFFFF',
    background:'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 80%)',
    opacity: '100%'
  }
}));

const NavButtonLeft = styled(MuiButton)(({ theme })=>({
  backgroundColor: '#FFFFFF',
  color: '#87ACB9',
  background:'linear-gradient(270deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 80%)',
  opacity: '0%',
  height: '85%',
  transition: 'opacity 0.2s',
  boxShadow: 'none',
  borderWidth: 0,
  '&:hover': {
    backgroundColor: '#FFFFFF',
    background:'linear-gradient(270deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 80%)',
    opacity: '100%'
  }
}));

const ThumbnailImage = styled('img')({
  width: '100%'
});



export const FC: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, groupName, loading } = dataService.usePitchDesignSection();
  const slideGroups: SlideGroupName[] = ['bySlideType', 'byPitchExample', 'otherResources'];
  const refs = React.useRef(slideGroups.map(_ => React.createRef<HTMLDivElement>()));

  const onClickSlide = React.useCallback((group: string, title: string) => {
    navigate(routeDirectory.pitchDecks.title.path(group, title), {
      state: {
        prevPath: location.pathname
      }
    });
  }, [location.pathname, navigate]);

  const handleScrollLeft = React.useCallback((ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollTo({ behavior: 'smooth', left: ref.current.scrollLeft - 440 });
    }
  }, []);

  const handleScrollRight = React.useCallback((ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollTo({ behavior: 'smooth', left: ref.current.scrollLeft + 440 });
    }
  }, []);

  return (
    <>
      <LoadingOverlay active={loading} />

      {_.map(slideGroups, group => (
        <CardRowBox key={group}>
          <SlideHeaderBox>
            <SlideHeaderTypography variant="h2">{groupName[group]}</SlideHeaderTypography>

            <Button
              variant="text"
              color="primary"
              href={routeDirectory.pitchDecks.group.path(group)}>
              SEE ALL
            </Button>
          </SlideHeaderBox>

          <SlideListBox>
            <NavButtonRight
              disableElevation
              style={{ position: 'absolute', right: 0, zIndex: 1 }}
              onClick={() => handleScrollRight(refs.current[slideGroups.indexOf(group)])}>
              <ArrowForwardIosIcon />
            </NavButtonRight>

            <NavButtonLeft
              disableElevation
              style={{ position: 'absolute', left: 0, zIndex: 1 }}
              onClick={() => handleScrollLeft(refs.current[slideGroups.indexOf(group)])}>
              <ArrowBackIosNewIcon />
            </NavButtonLeft>

            <RightMaskedStack
              ref={refs.current[slideGroups.indexOf(group)]}
              direction="row"
              spacing={3}>
              {data[group].map(slide => (
                <Stack
                  spacing={3}
                  key={slide.id}>
                  <RelativeImageWrapperBox>
                    <ShadowBox />
                    <ThumbnailBox onClick={() => onClickSlide(group, slide.title)}>
                      <ThumbnailImage
                        src={imageUtil.getFileUrl(slide.thumbnailId)}
                        alt={slide.title} />
                    </ThumbnailBox>
                  </RelativeImageWrapperBox>

                  <Typography
                    fontSize={20}
                    color="#1E3949">
                    {slide.title}
                  </Typography>
                </Stack>
              ))}
            </RightMaskedStack>
          </SlideListBox>
        </CardRowBox>
      ))}
    </>
  );
};
